import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import aboutHeroImg from "@/assets/about-hero.jpg";
import { APP_URL } from "@/lib/app-config";

const About = () => (
  <>
    <Helmet>
      <title>About Elara Health | Our Mission in Peptide Therapy</title>
      <meta name="description" content="Learn about Elara Health's mission to make physician-guided peptide therapy accessible, safe, and personalized for every patient." />
      <link rel="canonical" href={`${APP_URL}/about`} />
      <meta property="og:title" content="About Elara Health | Our Mission in Peptide Therapy" />
      <meta property="og:description" content="Learn about Elara Health's mission to make physician-guided peptide therapy accessible, safe, and personalized for every patient." />
      <meta property="og:url" content={`${APP_URL}/about`} />
      <meta property="og:image" content={`${APP_URL}/og-image.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "AboutPage",
            name: "About Elara Health",
            description: "Elara Health is a clinician-guided telehealth platform focused on metabolic health, longevity, and lasting transformation.",
            url: `${APP_URL}/about`,
            mainEntity: {
              "@type": "Organization",
              "@id": `${APP_URL}/#organization`,
              name: "Elara Health",
              url: APP_URL,
              foundingLocation: { "@type": "Place", name: "Long Island, New York" },
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is Elara Health?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Elara Health is a clinician-guided telehealth platform focused on metabolic health, longevity, and lasting transformation. We connect patients with licensed U.S. providers who design personalized treatment plans delivered to your door.",
                },
              },
              {
                "@type": "Question",
                name: "How does Elara Health work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Start with a quick online health assessment, then a licensed clinician reviews your profile and designs a personalized treatment plan. Prescription medications are fulfilled by U.S.-licensed compounding pharmacies and shipped directly to you.",
                },
              },
              {
                "@type": "Question",
                name: "Who are the providers at Elara Health?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "All Elara Health providers are licensed U.S. clinicians who specialize in metabolic health, endocrinology, and regenerative medicine. Every provider undergoes credential verification before joining the platform.",
                },
              },
              {
                "@type": "Question",
                name: "Is Elara Health available in my state?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Elara Health is expanding across the United States. Availability depends on provider licensing in your state. Complete the health assessment to check eligibility in your area.",
                },
              },
            ],
          },
        ],
      })}</script>
    </Helmet>
    <Navbar />
    <main className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto mb-16">
          <div className="space-y-6">
            <h1 className="font-display text-4xl md:text-5xl text-foreground">About Elara Health</h1>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Elara Health is a clinician-guided telehealth platform focused on metabolic health, longevity, and lasting transformation.
                We believe that transformative health outcomes require continuity, trust, and personalized clinical oversight.
              </p>
              <p>
                Elara is built for people who want more than a quick fix or a fragmented care experience. Through personalized care plans,
                expert clinical guidance, and ongoing support, we help patients move forward with greater clarity, confidence, and consistency.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src={aboutHeroImg}
              alt="Patient preparing peptide therapy at home in a calming, sage-toned environment"
              loading="lazy"
              width={1024}
              height={1024}
              className="rounded-2xl shadow-lg w-full h-auto object-cover aspect-[4/5]"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-4 text-lg text-muted-foreground">
          <p>
            Our team of licensed providers works directly with patients to design evidence-based therapy plans
            tailored to individual goals — from metabolic optimization to recovery and longevity.
          </p>
          <p>
            Based on Long Island, New York, Elara Health partners with licensed 503A compounding pharmacies
            to ensure the highest quality formulations delivered directly to your door.
          </p>
          <p className="italic text-foreground/80 font-display text-xl pt-4">
            Care that continues, even when insurance does not.
          </p>
        </div>
      </div>
    </main>
    <Footer />
  </>
);

export default About;
